import { useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { IPartialLangDict, ITranslation } from "@/queries/useTranslations";
import { useTranslations } from "@/queries/useTranslations";
import type { IState } from "@/state";
import type { IContentType } from "@/types";
import type { FilteredIds } from "@/translationFiltering";
import { filterAllIds } from "@/translationFiltering";
import type { VersionSearch } from "@/urlState";
import { canonicalSearchKey } from "@/urlState";

type EditsByContent = Partial<Record<IContentType, IPartialLangDict>> | undefined;

type FilterCache = {
    version: string;
    translations: ITranslation | undefined;
    compareTranslations: ITranslation | undefined;
    editsByContent: EditsByContent;
    searchKey: string;
    result: FilteredIds | null;
};

let lastCache: FilterCache | null = null;

function getFilteredIdsCached(args: {
    version: string;
    translations: ITranslation | undefined;
    compareTranslations: ITranslation | undefined;
    editsByContent: EditsByContent;
    search: VersionSearch;
    searchKey: string;
}): FilteredIds | null {
    const { version, translations, compareTranslations, editsByContent, search, searchKey } = args;
    if (!translations) {
        return null;
    }
    const compare = search.since ? compareTranslations : undefined;
    if (search.since && !compare) {
        return null;
    }
    if (
        lastCache &&
        lastCache.version === version &&
        lastCache.translations === translations &&
        lastCache.compareTranslations === compare &&
        lastCache.editsByContent === editsByContent &&
        lastCache.searchKey === searchKey
    ) {
        return lastCache.result;
    }
    const result = filterAllIds(translations, compare, editsByContent, search);
    lastCache = {
        version,
        translations,
        compareTranslations: compare,
        editsByContent,
        searchKey,
        result,
    };
    return result;
}

export function useFilteredIds(version: string): {
    filtered: FilteredIds | null;
    isComparePending: boolean;
} {
    const search = useSearch({
        from: "/$version",
        select: (state) => ({
            id: state.id,
            q: state.q,
            since: state.since,
            empty: state.empty,
            langs: state.langs,
            mods: state.mods,
        }),
    });
    const { data: translations } = useTranslations(version);
    const { data: compareTranslations, isPending: isComparePending } = useTranslations(search.since ?? "");
    const editsByContent = useSelector((state: IState) => state.edits[version]?.data);
    const searchKey = useMemo(() => canonicalSearchKey(search), [search]);

    const filtered = getFilteredIdsCached({
        version,
        translations,
        compareTranslations,
        editsByContent,
        search,
        searchKey,
    });

    return { filtered, isComparePending: !!search.since && isComparePending };
}
